// "Which Signal is this?" overlays for a generated mapping: pin an output to
// one of the destination channel's template Signals, or SPLIT it by a field -
// the Signal name is read from that field and each of its values is mapped to
// a template Signal (e.g. type: ram -> "RAM", hdd -> "Disk"). No new engine
// feature: a split is `{to:'name', from:<field>, map:{value: signalName}}`,
// which C, TypeScript and Go already evaluate identically (values without a
// map entry keep their own text as the Signal name).
import { resolvePath, type JsonValue, type MappingRule, type MappingSpec } from './mappingEngine';
import { getOutputs, setOutputRules } from './mappingSpecEdit';

export interface OutputOverlay {
  signal?: string; // template Signal name -> constant name
  splitBy?: string; // field path whose values name the Signal
  splitMap?: Record<string, string>; // value -> Signal name; '' = keep the value as-is
}

const MAX_VALUES = 30;

export function distinctValues(records: JsonValue[], path: string, max = MAX_VALUES): string[] {
  const seen = new Set<string>();
  for (const rec of records.slice(0, 200)) {
    const v = resolvePath(rec, path);
    if (v === undefined || v === null || typeof v === 'object') continue;
    const s = String(v).trim();
    if (s) seen.add(s);
    if (seen.size >= max) break;
  }
  return [...seen];
}

// A value that already equals a template Signal's name (ignoring case) is
// pre-mapped to it, so the common "the field holds the Signal names" case
// needs no clicks.
export function defaultSplitMap(values: string[], signalNames: string[]): Record<string, string> {
  const byLower = new Map(signalNames.map((n) => [n.toLowerCase(), n]));
  const out: Record<string, string> = {};
  for (const v of values) {
    const hit = byLower.get(v.toLowerCase());
    if (hit) out[v] = hit;
  }
  return out;
}

const safeKey = (s: string) => s.replace(/[^A-Za-z0-9_]/g, '_').replace(/^_+|_+$/g, '') || 'field';

export function effectiveSplitMap(values: string[], signalNames: string[], user: Record<string, string> | undefined): Record<string, string> {
  const merged: Record<string, string> = { ...defaultSplitMap(values, signalNames), ...(user ?? {}) };
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(merged)) if (v && v !== k) out[k] = v;
  return out;
}

export function applyOverlays(
  spec: MappingSpec,
  overlays: Record<number, OutputOverlay | undefined>,
  ctx: { records: JsonValue[]; signalNames: string[]; signalRefs?: Record<string, string> },
): MappingSpec {
  let out = spec;
  getOutputs(spec).forEach((rules, o) => {
    const ov = overlays[o];
    if (!ov || (!ov.signal && !ov.splitBy)) return;
    const nameIdx = rules.findIndex((r) => r.to === 'name');
    const prevName = nameIdx >= 0 ? rules[nameIdx] : undefined;
    let next: MappingRule[] = rules.filter((_, i) => i !== nameIdx);
    let nameRule: MappingRule;
    if (ov.splitBy) {
      // Kept on `to: 'name'` even when signalRefs are known - unlike the
      // single-signal pick below, switching this map wholesale to
      // `signalRef` would mean every unmapped split value (one with no
      // matching existing Signal) silently auto-creates a brand new
      // template signal (docs/SIGNAL_TEMPLATES_AS_MAPPING_SCHEMA_PLAN.md
      // Phase 1's ResolveOrCreate) instead of just naming the Signal from
      // its own raw value as today - a real behavior change deserving its
      // own decision, not a corner cut into this pass.
      const map = effectiveSplitMap(distinctValues(ctx.records, ov.splitBy), ctx.signalNames, ov.splitMap);
      nameRule = { to: 'name', from: ov.splitBy, ...(Object.keys(map).length ? { map } : {}) };
      // The field now names the Signal, so it stops being a label; the field
      // that used to name it (if different) is kept as a label instead.
      next = next.filter((r) => r.from !== ov.splitBy);
      if (prevName?.from && prevName.from !== ov.splitBy) next.push({ to: `labels.${safeKey(prevName.from.split('.').pop() ?? prevName.from)}`, from: prevName.from });
    } else {
      // Phase 3 of docs/SIGNAL_TEMPLATES_AS_MAPPING_SCHEMA_PLAN.md: pinning
      // an output to one specific existing template Signal writes
      // `to: "signalRef"` targeting that signal's own global `number`
      // (Phase 2's rule target) instead of a free-typed `to: "name"` const
      // string, whenever that number is known - names stay unambiguous
      // across every mapping feeding the same signal instead of two
      // mappings spelling the same metric two different ways (the plan's
      // own motivating example). Falls back to the old `to: "name"` const
      // when ov.signal doesn't resolve to a real number (e.g. mid-load,
      // before `useChannelTemplates` has hydrated) - never a broken rule.
      const ref = ov.signal ? ctx.signalRefs?.[ov.signal] : undefined;
      nameRule = ref ? { to: 'signalRef', const: ref } : { to: 'name', const: ov.signal };
    }
    out = setOutputRules(out, o, [nameRule, ...next]);
  });
  return out;
}
